import "./MyFirstRealVibe.scss";

import { ReactNode } from "react";

import PostWrapper from "../PostWrapper";
import { PostCategory } from "../PostsHome";

type VibeMessageProps = {
  children: ReactNode;
  from: "Ryan" | "Codex";
};

function VibeMessage(props: VibeMessageProps) {
  const { children, from } = props;
  const side = from === "Ryan" ? "ryan" : "codex";

  return (
    <div className={`vibe-message vibe-message--${side}`}>
      <div className="vibe-message-author">{from}</div>
      <div className="vibe-message-body">{children}</div>
    </div>
  );
}

function MyFirstRealVibeScreen() {
  return (
    <PostWrapper
      category={PostCategory.HowTo}
      subTitle="Talking back and forth with my robot colleague"
      title="My first real vibe"
    >
      <p>
        I've been using AI tools for a while, but never truly in the "vibe" way.
      </p>

      <p>
        My initial use of AI tools has been more specific prompts for a known
        task, which created a small update that was easy to slot into my
        existing code.
      </p>

      <p>
        Or the "god-mode" level of auto-complete through prompting via inline
        comments or method names and having auto-complete generate pretty solid
        first drafts of new code. Which is extra awesome when it comes to
        writing tests (...or not writing tests, just writing test cases and
        having AI generate the whole test 🎉)
      </p>

      <p>This was different.</p>

      <p>
        This was a session that felt more like collaboration than prompting. It
        was fun.
      </p>

      <p>
        I did still review the changes at every step, and (when I remembered) I
        kept the commits separate so it's easy to follow along in that way as
        well. (I'll add in some links below to those commits). So maybe not yet
        the full "vibe and ride" that it seems others are doing, but vibing none
        the less.
      </p>

      <p>What follows is the vibe conversation that did the following:</p>

      <ul>
        <li>
          Removed the auto-checking of the cook mode option (since I realized
          that may annoy some users 😅)
        </li>
        <li>Created the base setup for the Useful Tools & Code section</li>
        <li>Noticed a thing it added for categories that I liked</li>
        <li>
          Vibed back and forth about categories, making things reusable, adding
          in functionality (filtering), and implementing that for posts and
          tools sections
        </li>
        <li>
          Had an image made for the empty state of filtering to a category with
          no pages... just because I was curious how that would go
        </li>
        <li>Created this post</li>
      </ul>

      <p>Here are those links to the commits:</p>
      <ul>
        <li>TODO: need to push things first</li>
      </ul>

      <h3>Everything written below was generated by OpenAI Codex</h3>

      <p>
        NOTE: I did update the prompts from my side to match my actual prompts
        (Codex made some adjustments when it wrote them 🤷).
      </p>

      <p>
        The useful part of this session was not one single command. It was the
        rhythm: Ryan described what he wanted, Codex inspected the code, made a
        small change, verified it, and then adjusted when Ryan pushed the idea a
        little further. That back-and-forth is where the work started to feel
        like collaboration instead of a one-shot prompt.
      </p>

      <div className="vibe-thread" aria-label="Conversation highlights">
        <VibeMessage from="Ryan">
          Remove the auto-checking of the cook mode option, but keep turning it
          off when the user leaves a recipe page.
        </VibeMessage>
        <VibeMessage from="Codex">
          I changed the footer effect so route changes can only disable cook
          mode, while the checkbox remains the only way to enable it.
        </VibeMessage>

        <VibeMessage from="Ryan">
          Create the path and related components for Useful Tools & Code so we
          are ready to add content pages.
        </VibeMessage>
        <VibeMessage from="Codex">
          I mirrored the blog post setup with a new route, home screen, wrapper,
          preview tile, and starter categories for snippets, tools, references,
          and utility methods.
        </VibeMessage>

        <VibeMessage from="Ryan">
          That categories UI is likely reusable. Extract a shared component and
          let it filter the shown content.
        </VibeMessage>
        <VibeMessage from="Codex">
          I pulled the badge row into a shared category filter, turned the
          badges into accessible toggle buttons, and moved both pages to
          data-driven filtered previews.
        </VibeMessage>

        <VibeMessage from="Ryan">
          Can we make the active effect cooler?
        </VibeMessage>
        <VibeMessage from="Codex">
          I replaced the plain active ring with a lifted badge, stronger
          saturation, layered shadow, and a floating check marker.
        </VibeMessage>

        <VibeMessage from="Ryan">
          Add an image for the "Nothing here yet." state, with alt text for
          accessibility users. Make it geeky pop and 4:3.
        </VibeMessage>
        <VibeMessage from="Codex">
          I generated a 4:3 empty coding-stage illustration, saved it as a
          shared asset, and wrapped it in a reusable empty-state component with
          descriptive alt text.
        </VibeMessage>

        <VibeMessage from="Ryan">
          Very nice! Last thing I want to do in this session is create a posts
          titled "My first real vibe" (subtitled "Talking back and forth with my
          robot colleague") that will be a "how to" post.
        </VibeMessage>
        <VibeMessage from="Codex">
          I added the new how-to post, routed it, made it the latest post, and
          shaped the generated section as a conversation recap with message-like
          styling.
        </VibeMessage>
      </div>

      <p>
        The pattern is simple, but it matters: ask for behavior, inspect the
        existing system, make the smallest useful change, verify, and keep
        talking. The more concrete the next prompt gets, the more the work
        starts to look like pair programming.
      </p>

      <p className="vibe-handshake" aria-label="handshake">
        🤝
      </p>
    </PostWrapper>
  );
}

export default MyFirstRealVibeScreen;
